import type { PaymentInstructions } from '@hue/contracts';
import { Button, DocumentInput, Input, Combobox, useSnackbar } from '@hue/design-system';
import { useState } from 'react';

const methods = [
  { value: '', label: 'Não informar' },
  { value: 'PIX', label: 'Pix' },
  { value: 'BOLETO', label: 'Boleto' },
  { value: 'TRANSFER', label: 'Transferência bancária' },
];

function ChoiceField({
  label,
  options,
  value,
  onValueChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onValueChange: (value: string) => void;
}) {
  return (
    <Combobox
      label={label}
      placeholder="Escolher"
      options={options}
      selectedLabel={options.find((option) => option.value === value)?.label ?? ''}
      clearable={false}
      onSelect={(choice) => {
        if (choice.kind === 'option') onValueChange(choice.value);
      }}
    />
  );
}

export function PaymentInstructionsFields({
  value,
  onChange,
  recurring = false,
}: {
  value: PaymentInstructions | null;
  onChange: (value: PaymentInstructions | null) => void;
  recurring?: boolean;
}) {
  const [documentKind, setDocumentKind] = useState<'CPF' | 'CNPJ'>(
    value?.method === 'TRANSFER' && (value.holderDocument?.length ?? 0) > 11 ? 'CNPJ' : 'CPF',
  );
  function field(key: string, label: string, maxLength: number, numeric = false) {
    const current = value as unknown as Record<string, string>;
    return (
      <Input
        label={`${label} (opcional)`}
        value={current[key] ?? ''}
        maxLength={maxLength}
        inputMode={numeric ? 'numeric' : 'text'}
        onChange={(event) =>
          onChange({
            ...value,
            [key]: numeric ? event.target.value.replace(/[.\s-]/gu, '') : event.target.value,
          } as PaymentInstructions)
        }
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.preventDefault();
        }}
      />
    );
  }
  return (
    <fieldset className="mt-5 min-w-0 space-y-4 rounded-xl border border-surface-border p-4">
      <legend className="px-1 text-sm font-semibold">Dados para pagamento (opcional)</legend>
      <p className="text-sm text-content-muted">
        Use para guardar a chave Pix, o código do boleto ou os dados bancários de quem vai receber.
        Depois, é só copiar esses dados e pagar no seu banco. O sistema não faz o pagamento
        {recurring ? '.' : ', e comprovantes não ficam aqui.'}
      </p>
      <ChoiceField
        label="Forma de pagamento (opcional)"
        options={methods}
        value={value?.method ?? ''}
        onValueChange={(method) => onChange(method ? ({ method } as PaymentInstructions) : null)}
      />
      {value?.method === 'PIX' ? (
        <>
          {field('key', 'Chave Pix', 160)}
          {!recurring ? field('copyPaste', 'Pix copia e cola', 4096) : null}
        </>
      ) : null}
      {value?.method === 'BOLETO' && !recurring ? (
        <>
          {field('documentNumber', 'Número do documento', 160)}
          {field('barcode', 'Linha digitável ou código de barras', 80, true)}
          <p className="text-sm text-content-muted">
            Clique no campo e use um leitor que digita como teclado. Enter encerra a leitura sem
            salvar o formulário. O número do documento não substitui o código para pagamento.
          </p>
        </>
      ) : null}
      {value?.method === 'TRANSFER' ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {field('bank', 'Banco ou instituição', 160)}
          {field('branch', 'Agência com dígito, se houver', 30)}
          {field('accountNumber', 'Número da conta bancária com dígito', 40)}
          <ChoiceField
            label="Tipo da conta bancária (opcional)"
            value={value.accountType ?? ''}
            options={[
              { value: '', label: 'Não informar' },
              { value: 'CHECKING', label: 'Corrente' },
              { value: 'SAVINGS', label: 'Poupança' },
              { value: 'PAYMENT', label: 'Pagamento' },
            ]}
            onValueChange={(kind) => {
              const rest = { ...value };
              delete rest.accountType;
              onChange(
                kind ? { ...rest, accountType: kind as 'CHECKING' | 'SAVINGS' | 'PAYMENT' } : rest,
              );
            }}
          />
          {field('holder', 'Titular', 160)}
          <div className="space-y-3">
            <ChoiceField
              label="Tipo de documento do titular"
              value={documentKind}
              options={[
                { value: 'CPF', label: 'CPF' },
                { value: 'CNPJ', label: 'CNPJ' },
              ]}
              onValueChange={(kind) => {
                setDocumentKind(kind as 'CPF' | 'CNPJ');
                onChange({ ...value, holderDocument: '' });
              }}
            />
            <DocumentInput
              kind={documentKind}
              label={`${documentKind} do titular (opcional)`}
              value={value.holderDocument ?? ''}
              onValueChange={(holderDocument) =>
                onChange({ ...value, holderDocument: holderDocument.replace(/\D/gu, '') })
              }
            />
          </div>
          {field('identification', 'Identificação exigida pelo favorecido', 160)}
          <p className="text-sm text-content-muted sm:col-span-2">
            Use a identificação somente quando o favorecido fornecer uma referência para informar no
            banco.
          </p>
        </div>
      ) : null}
      {recurring && value ? (
        <p className="text-sm text-content-muted">
          A forma de pagamento e os dados permanentes são copiados para cada nova despesa. Cadastre
          boleto e Pix copia e cola na despesa de cada mês para evitar reutilizar uma cobrança.
        </p>
      ) : null}
    </fieldset>
  );
}

export function PaymentInstructionsDetails({
  value,
}: {
  value?: PaymentInstructions | null | undefined;
}) {
  const snackbar = useSnackbar();
  if (!value) return null;
  const labels: Record<string, string> = {
    key: 'Chave Pix',
    copyPaste: 'Pix copia e cola',
    documentNumber: 'Número do documento',
    barcode: 'Linha digitável ou código de barras',
    bank: 'Banco ou instituição',
    branch: 'Agência',
    accountNumber: 'Número da conta bancária',
    holder: 'Titular',
    holderDocument: 'CPF/CNPJ do titular',
    identification: 'Identificação exigida pelo favorecido',
    accountType: 'Tipo da conta bancária',
  };
  const accountTypes: Record<string, string> = {
    CHECKING: 'Corrente',
    SAVINGS: 'Poupança',
    PAYMENT: 'Pagamento',
  };
  return (
    <section
      aria-label="Dados para pagamento"
      className="mt-5 min-w-0 space-y-4 rounded-xl border border-surface-border p-4"
    >
      <h3 className="font-semibold">
        Dados para pagamento · {methods.find((method) => method.value === value.method)!.label}
      </h3>
      <p className="text-sm text-content-muted">
        Confira o favorecido e os valores no banco antes de confirmar o pagamento.
      </p>
      {Object.entries(value)
        .filter(([key, content]) => key !== 'method' && content)
        .map(([key, content]) => {
          const text = key === 'accountType' ? accountTypes[content ?? '']! : (content ?? '');
          return (
            <div key={key} className="flex min-w-0 flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs text-content-muted">{labels[key]}</p>
                <p className="break-all text-sm">{text}</p>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  void (async () => {
                    try {
                      await navigator.clipboard.writeText(text);
                      snackbar.success('Dado copiado.');
                    } catch {
                      snackbar.failure(
                        'Não foi possível copiar. Selecione o texto e copie manualmente.',
                      );
                    }
                  })();
                }}
              >
                Copiar {labels[key]}
              </Button>
            </div>
          );
        })}
    </section>
  );
}
